import React, { useEffect, useRef, useState } from "react";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import { IoAlertCircleOutline } from "react-icons/io5";
import { mdiDotsHorizontal } from "@mdi/js";
import Main from "../../components/layout/Main";
import MdiIcon from "../../components/device/app/MdiIcon";
import { useToggleTop } from "../../components/common/ToggleTop";
import { REMOTE_TYPES } from "../../constants/devices";
import { sendControl } from "../../services/deviceControl";
import { hapticTap } from "../../preferences/feedback";
import { usePropertyRole } from "../../hooks/usePropertyOwner";
import { UserService } from "../../services/UserService";
import { User } from "../../storage/store";
import AcRemote from "./AcRemote";
import EditRemoteDialog from "./EditRemoteDialog";
import { KeyLayout, NumberPad } from "./RemoteKeys";
import { LAYOUTS, hasDimmedKeys } from "./layouts";
import { REMOTE_PATHS, blastPayload } from "./remoteUtils";
import "../../assets/styles/app-device.css";
import "../../assets/styles/app-remotes.css";

// One remote learned on an IR blaster (app/screens/devices/RemoteLayoutView.js),
// with the app's "⋯" Edit Remote for owners. The app's Relearn Remote isn't
// here: it finds the blaster on the phone's Wi-Fi and records keys over the
// local network, which a web page can't reach.
// Location state: { data: the IRBM device, remote: one of its remoteDevices }.
function RemoteView() {
  const { pathname, state } = useLocation();
  const history = useHistory();
  const device = state?.data;
  const remote = state?.remote;
  const remoteId = remote?.remoteId;
  const toast = useToggleTop();
  const role = usePropertyRole();
  const [keys, setKeys] = useState(null);
  const [status, setStatus] = useState("loading");
  const [attempt, setAttempt] = useState(0);
  const [numbersOpen, setNumbersOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const menuRef = useRef(null);

  // Close the more menu on an outside click or Escape.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (event) => {
      if (event.type === "keydown" ? event.key === "Escape" : !menuRef.current?.contains(event.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
    };
  }, [menuOpen]);

  // Like the app, the key codes come from the decoded remote
  // (irdb/remote/{propertyId}/data/{remoteId}), not from the config.
  useEffect(() => {
    if (!remoteId) return undefined;
    let live = true;
    setStatus("loading");
    UserService.decodeRemoteById(User.getPropertyId(), remoteId)
      .then((response) => {
        if (!live) return;
        setKeys(response?.keys ?? {});
        setStatus("ready");
      })
      .catch(() => {
        if (live) setStatus("error");
      });
    return () => {
      live = false;
    };
  }, [remoteId, attempt]);

  if (!device || !remote) return <Redirect to="/dashboard" />;

  const type = remote.deviceType?.toUpperCase();
  const rows = LAYOUTS[type];
  const title = remote.name || "Remote";
  const loadStatus = remoteId ? status : "error";
  // The app's RemotesList hands the blaster's applianceId to the remote as its IRBM id.
  const irbmId = device.applianceId ?? device.deviceId;

  const codeFor = (name) =>
    loadStatus === "ready" && keys && Object.prototype.hasOwnProperty.call(keys, name) ? keys[name] || null : null;
  const isLearned = (name) => Boolean(codeFor(name));

  const press = async (name) => {
    const code = codeFor(name);
    if (!code) {
      toast("Key not assigned. Relearn it in the HASHh Connect app.", "blue");
      return;
    }
    hapticTap();
    try {
      await sendControl(blastPayload(irbmId, code));
    } catch (error) {
      toast("Couldn't send the command", "red");
    }
  };

  const editRemote = () => {
    setMenuOpen(false);
    if (role === "guest") {
      toast("You have to be a owner to edit the device.", "red");
      return;
    }
    setEditing(true);
  };

  // A renamed remote keeps its page, under the new name; a deleted one goes back.
  const renamed = (name) => history.replace(pathname, { ...state, remote: { ...remote, name } });
  const deleted = () => (history.length > 1 ? history.goBack() : history.push(REMOTE_PATHS.UNIVERSAL));

  const moreMenu = (
    <div ref={menuRef} style={{ position: "relative" }}>
      <button
        type="button"
        className="hx-round"
        aria-label="More options"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen(!menuOpen)}
      >
        <MdiIcon path={mdiDotsHorizontal} size={28} />
      </button>
      {menuOpen ? (
        <div className="hx-pop" role="menu">
          <button type="button" role="menuitem" onClick={editRemote}>
            Edit Remote
          </button>
        </div>
      ) : null}
    </div>
  );

  return (
    <Main header="stack" title={title} headerRight={moreMenu}>
      <section className="hx-remote" aria-label={`${title} remote`} aria-busy={loadStatus === "loading"}>
        {loadStatus === "loading" ? (
          <p className="hx-remote-status" role="status">
            Loading the remote's keys…
          </p>
        ) : null}
        {loadStatus === "error" ? (
          <div className="hx-remote-status" role="alert">
            <IoAlertCircleOutline aria-hidden="true" />
            <span>Couldn't load this remote's keys.</span>
            {remoteId ? (
              <button type="button" className="hx-btn" onClick={() => setAttempt((count) => count + 1)}>
                Try again
              </button>
            ) : null}
          </div>
        ) : null}

        {type === REMOTE_TYPES.AC ? (
          <AcRemote key={remoteId} remoteId={remoteId} isLearned={isLearned} onKey={press} />
        ) : rows ? (
          <KeyLayout
            rows={rows}
            isLearned={isLearned}
            onKey={press}
            onNumbers={() => setNumbersOpen(true)}
            numbersOpen={numbersOpen}
          />
        ) : (
          <div className="hx-empty">This type of remote can't be shown here yet.</div>
        )}

        {loadStatus === "ready" && hasDimmedKeys(type, isLearned) ? (
          <p className="hx-remote-note">
            Dimmed keys aren't learned on this remote. You can learn them in the HASHh Connect app.
          </p>
        ) : null}
      </section>
      {rows ? (
        <NumberPad open={numbersOpen} onClose={() => setNumbersOpen(false)} isLearned={isLearned} onKey={press} />
      ) : null}
      <EditRemoteDialog
        open={editing}
        blaster={device}
        remote={remote}
        onClose={() => setEditing(false)}
        onRenamed={renamed}
        onDeleted={deleted}
      />
    </Main>
  );
}

export default RemoteView;
